import * as React from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid2';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import Chart from "react-apexcharts";
import { ApexOptions } from 'apexcharts';
import LoadingData from './LoadingData';
import { Box } from '@mui/material';
import { createDateTimeChartConfig, createYAxisConfig, ChartColors } from './shared/ChartConfig';
import ColorGuide from './ColorGuide';

interface CarbonIntensityItem {
  timestamp: string;
  intensity: number;
  category: number;
  intensity_delta?: number;
}

interface CarbonIntensityChartProps { 
  loading: boolean;
  data?: CarbonIntensityItem[];
  tariffInfo?: string;
  regionName?: string;
  showDeltaChart?: boolean;
  'data-chart-id'?: string;
}

// Helper function to find 2-hour periods for carbon intensity
const findTwoHourCarbonPeriods = (timestamps: Date[], intensities: number[]) => {
  const periods: {start: Date, end: Date, averageIntensity: number}[] = [];
  const durationMs = 2 * 60 * 60 * 1000; // 2 hours in milliseconds

  for (let i = 0; i < timestamps.length - 1; i++) {
    const startTime = timestamps[i];
    const targetEndTime = new Date(startTime.getTime() + durationMs);
    
    // Find the last data point within this period
    let lastIndex = i;
    for (let j = i + 1; j < timestamps.length; j++) {
      const currentTime = timestamps[j];
      if (currentTime < targetEndTime) {
        lastIndex = j;
      } else {
        break;
      }
    }

    // Calculate average intensity for this period
    const periodData = intensities.slice(i, lastIndex + 1);
    const averageValue = periodData.reduce((sum, intensity) => sum + intensity, 0) / periodData.length;

    periods.push({
      start: startTime,
      end: targetEndTime,
      averageIntensity: averageValue
    });
  }

  return periods;
};

const CarbonIntensityChart: React.FC<CarbonIntensityChartProps> = ({ loading, data, regionName, showDeltaChart = true, 'data-chart-id': dataChartId}) => {
  const [nowTime, setNowTime] = React.useState<Date>(new Date());
  const [xData, setCarbonXData] = React.useState<Date[]>([]);
  const [minTimestamp, setMinTimestamp] = React.useState<number>(0);
  const [maxTimestamp, setMaxTimestamp] = React.useState<number>(0);
  const [intensityValues, setCarbonIntensityValues] = React.useState<number[]>([]);
  const [deltaData, setDeltaData] = React.useState<number[]>([]);
  const [hasDeltaData, setHasDeltaData] = React.useState<boolean>(false);
  const [cleanestPeriod, setCleanestPeriod] = React.useState<{start: Date, end: Date, intensity: number} | null>(null);
  const [dirtiestPeriod, setDirtiestPeriod] = React.useState<{start: Date, end: Date, intensity: number} | null>(null);
  const [currentIntensity, setCurrentIntensity] = React.useState<number>(0);
  const [nextIntensity, setNextIntensity] = React.useState<number>(0);
  const [currentCategory, setCurrentCategory] = React.useState<number>(3);
  const [nextCategory, setNextCategory] = React.useState<number>(3);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Process carbon intensity data
  React.useEffect(() => {
    if (data && data.length > 0) {
      const timestamps = data.map(item => new Date(item.timestamp));
      const intensities = data.map(item => item.intensity);
      const categories = data.map(item => item.category);
      const deltas = data.map(item => item.intensity_delta || 0);
      
      // Check if we have delta data
      const hasDelta = data.some(item => item.intensity_delta !== undefined);
      
      setCarbonXData(timestamps);
      setCarbonIntensityValues(intensities);
      setDeltaData(deltas);
      setHasDeltaData(hasDelta);
      setNowTime(new Date());
      
      // Calculate min/max timestamps from carbon intensity data
      const minTime = Math.min(...timestamps.map(t => t.getTime()));
      const maxTime = Math.max(...timestamps.map(t => t.getTime()));
      setMinTimestamp(minTime);
      setMaxTimestamp(maxTime);
      
      // Find current and next carbon intensity
      const now = new Date();
      let currentIntensity = 0;
      let nextIntensity = 0;
      let currentCategory = 3;
      let nextCategory = 3;
      
      for (let i = 0; i < timestamps.length; i++) {
        const timestamp = timestamps[i];
        const nextTimestamp = i < timestamps.length - 1 ? timestamps[i + 1] : null;
        
        // Current intensity: intensity for the period that contains "now"
        if (now >= timestamp && (!nextTimestamp || now < nextTimestamp)) {
          currentIntensity = intensities[i];
          currentCategory = categories[i];
        }
        
        // Next intensity: intensity for the next period after current
        if (now < timestamp && nextIntensity === 0) {
          nextIntensity = intensities[i];
          nextCategory = categories[i];
          break;
        }
      }
      
      setCurrentIntensity(currentIntensity);
      setNextIntensity(nextIntensity);
      setCurrentCategory(currentCategory);
      setNextCategory(nextCategory);

      // Find cleanest and dirtiest 2-hour periods
      const twoHourPeriods = findTwoHourCarbonPeriods(timestamps, intensities);
      if (twoHourPeriods.length > 0) {
        const cleanest = twoHourPeriods.reduce((min, period) => 
          period.averageIntensity < min.averageIntensity ? period : min
        );
        const dirtiest = twoHourPeriods.reduce((max, period) => 
          period.averageIntensity > max.averageIntensity ? period : max
        );
        
        setCleanestPeriod({
          start: cleanest.start,
          end: cleanest.end,
          intensity: cleanest.averageIntensity
        });
        setDirtiestPeriod({
          start: dirtiest.start,
          end: dirtiest.end,
          intensity: dirtiest.averageIntensity
        });
      } else {
        setCleanestPeriod(null);
        setDirtiestPeriod(null);
      }
    } else {
      setCarbonXData([]);
      setCarbonIntensityValues([]);
      setDeltaData([]);
      setHasDeltaData(false);
      setCurrentIntensity(0);
      setNextIntensity(0);
      setCurrentCategory(0);
      setNextCategory(0);
      setMinTimestamp(0);
      setMaxTimestamp(0);
      setCleanestPeriod(null);
      setDirtiestPeriod(null);
    }
  }, [data]);


  // Helper function to get carbon intensity category based on intensity value
  // These thresholds should match the actual data's category thresholds
  const getCarbonCategory = (intensity: number): number => {
    if (intensity < 50) return 1; // Very Low
    if (intensity < 100) return 2; // Low
    if (intensity < 200) return 3; // Moderate
    if (intensity < 300) return 4; // High
    return 5; // Very High
  };

  const getColorForCarbonCateory = (category: number) => {
    // Carbon intensity color thresholds (g CO₂/kWh)
    switch (category) {
      case 1:
        return '#00cc00';     // Green for verylow rates
      case 2:
        return '#ffeb00';     // Yellow for low rates
      case 3:
        return '#ffa500';     // Orange for moderate rates
      case 4:
        return '#ff0000';     // Red for high rates
      case 5:
        return '#800080';     // Purple for very high rates
      default:
        return '#ffa500';    // Orange for other rates
    }
  };

  const isHistoricalPeriod = (timestamp: Date) => {
    const now = new Date();
    const periodEnd = new Date(timestamp.getTime() + 30 * 60 * 1000);
    return now >= periodEnd;
  };

  const getColorForIntensity = (category: number, timestamp: Date) => {
    if (isHistoricalPeriod(timestamp)) {
      return '#808080';
    }
    return getColorForCarbonCateory(category);
  };

  const getColorForDelta = (delta: number, intensity: number) => {
    if (delta === 0) return '#808080'; // Grey for no change
    
    const percentage = Math.abs(delta / intensity) * 100;
    
    if (delta > 0) {
      // Positive delta (current intensity higher than average - worse)
      if (percentage < 10) return '#ffa500';    // Orange
      if (percentage < 25) return '#ff0000';   // Red
      return '#800080';                        // Purple
    } else {
      // Negative delta (current intensity lower than average - better)
      if (percentage < 10) return '#ffeb00';    // Yellow
      if (percentage < 25) return '#00cc00';    // Green
      return '#6666ff';                        // Blue
    }
  };

  const carbonIntensityColorGuide = [
    { color: '#00cc00', label: 'V. Low' },
    { color: '#ffeb00', label: 'Low' },
    { color: '#ffa500', label: 'Moderate' },
    { color: '#ff0000', label: 'High' },
    { color: '#800080', label: 'V. High' }
  ];
  
  const carbonIntensityDeltaColorGuide = [
    { color: '#6666ff', label: 'Much cleaner' },
    { color: '#00cc00', label: 'Cleaner' },
    { color: '#ffeb00', label: 'A little cleaner' },
    { color: '#ffa500', label: 'A little dirtier' },
    { color: '#ff0000', label: 'Dirtier' },
    { color: '#800080', label: 'Much dirtier' }
  ];


  // Carbon intensity chart options
  const options: ApexOptions = {
    ...createDateTimeChartConfig({
      theme,
      chartType: 'bar',
      minTimestamp,
      maxTimestamp,
      nowTime,
      showNowMarker: true,
      tickAmount: 8,
      isMobile
    }),
    colors: ChartColors.carbonIntensity,
    chart: {
      ...createDateTimeChartConfig({
        theme,
        chartType: 'bar',
        minTimestamp,
        maxTimestamp,
        nowTime,
        showNowMarker: true,
        tickAmount: 8,
        isMobile
      }).chart,
      zoom: {
        enabled: false
      }
    },
    yaxis: createYAxisConfig(theme, 'Intensity (g / kWh)', {
      min: 0,
      tickAmount: 5,
      isMobile
    }),
    tooltip: {
      theme: theme.palette.mode,
      x: {
        formatter: function(value: number) {
          const date = new Date(value);
          return date.toLocaleString('en-GB', { 
            hour: '2-digit', 
            minute: '2-digit' 
          });
        }
      },
      y: {
        formatter: function(value: number) {
          return `${Math.round(value)} g / kWh`;
        }
      }
    }
  };

  const series = [
    {
      name: "Carbon Intensity",
      type: "bar",
      data: intensityValues.map((intensity, index) => ({
        x: xData[index].getTime() + (15 * 60 * 1000),
        y: intensity,
        fillColor: getColorForIntensity(data?.[index]?.category || 3, xData[index])
      }))
    }
  ];

  // Delta chart options and series
  const deltaOptions: ApexOptions = {
    ...createDateTimeChartConfig({
      theme,
      chartType: 'bar',
      minTimestamp,
      maxTimestamp,
      nowTime,
      showNowMarker: true,
      tickAmount: 8,
      isMobile
    }),
    colors: ChartColors.carbonIntensity,
    chart: {
      ...createDateTimeChartConfig({
        theme,
        chartType: 'bar',
        minTimestamp,
        maxTimestamp,
        nowTime,
        showNowMarker: true,
        tickAmount: 8,
        isMobile
      }).chart,
      zoom: {
        enabled: false
      }
    },
    yaxis: createYAxisConfig(theme, '+/- average', {
      min: -Math.max(Math.abs(Math.min(0, ...deltaData)), Math.abs(Math.max(0, ...deltaData))),
      max: Math.max(Math.abs(Math.min(0, ...deltaData)), Math.abs(Math.max(0, ...deltaData))),
      tickAmount: 2,
      decimalsInFloat: 0,
      isMobile
    }),
    tooltip: {
      theme: theme.palette.mode,
      x: {
        formatter: function(value: number) {
          const date = new Date(value - (15 * 60 * 1000)); // Add back 15 minutes to show original timestamp
          return date.toLocaleString('en-GB', {
            weekday: 'short',
            day: 'numeric',
            month: 'short',
            hour: '2-digit', 
            minute: '2-digit' 
          });
        }
      },
      y: {
        formatter: function(value: number) {
          return `${value > 0 ? '+' : ''}${value.toFixed(0)}g CO₂/kWh`;
        }
      }
    }
  };

  const deltaSeries = [
    {
      name: "Difference compared to normal",
      type: "bar",
      data: deltaData.map((delta, index) => ({
        x: xData[index].getTime() + (15 * 60 * 1000), // Shift visual position by +15 minutes
        y: delta,
        fillColor: getColorForDelta(delta, intensityValues[index])
      }))
    }
  ];

  return (
    <Card sx={{ width: '100%', position: 'relative', backgroundColor: theme.palette.background.paper }}>
      <CardContent>
        <LoadingData open={loading} />        
        {/* Carbon Intensity Chart */}
        {data && data.length > 0 && (
          <>
          <Box sx={{ px: { xs: 1, md: 0 } }}>
            <Typography variant="h3" gutterBottom sx={{ mb: 2 }}>
              Carbon Intensity ({regionName})
            </Typography>
            
            {/* Current and Next Carbon Intensity Display */}
            <Stack direction="row" spacing={4} sx={{ mb: 2 }}>
              <Box>
                <Typography variant="h5" color="text.secondary" sx={{ fontSize: '14px' }}>
                  Current Carbon Intensity
                </Typography>
                <Typography 
                  variant="h3" 
                  sx={{ 
                    fontWeight: 'bold',
                    color: getColorForCarbonCateory(currentCategory),
                    fontSize: '24px'
                  }}
                >
                  {currentIntensity.toFixed(0)} g/kWh
                </Typography>
              </Box>
              <Box>
                <Typography variant="h5" color="text.secondary" sx={{ fontSize: '14px' }}>
                  Next Carbon Intensity
                </Typography>
                <Typography 
                  variant="h3" 
                  sx={{ 
                    fontWeight: 'bold',
                    color: getColorForCarbonCateory(nextCategory),
                    fontSize: '24px'
                  }}
                >
                  {nextIntensity.toFixed(0)} g/kWh
                </Typography>
              </Box>
            </Stack>
            </Box>
            
            <Box sx={{ mt: 2 }}>
              <Chart
                options={options}
                series={series}
                type="bar"
                height={225}
                data-chart-id="carbon-intensity-chart"
              />

              <ColorGuide 
                items={carbonIntensityColorGuide}
              />
              
              {/* Delta Chart - only show if delta data exists and showDeltaChart is true */}
              {hasDeltaData && showDeltaChart && (
                <>
                  <Box sx={{ mt: -3 }}>
                    <Chart
                      options={deltaOptions}
                      series={deltaSeries}
                      type="bar"
                      height={150}
                      data-chart-id={`${dataChartId}-delta`}
                    />
                  </Box>
                  <ColorGuide 
                    items={carbonIntensityDeltaColorGuide}
                  />
                </>
              )}
              
              {/* Cleanest and Dirtiest Period Cards */}
              {(cleanestPeriod || dirtiestPeriod) && (
                <Box sx={{mx: {xs: 1, md: 0}}}>
                  <Typography variant="h5" sx={{ mt: 0, mb: 2 }}>Cleanest and Dirtiest 2-hour periods</Typography>
                  <Grid container spacing={2} sx={{ mt: 2, mb: 2 }}>
                    {cleanestPeriod && (
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Card sx={{
                          backgroundColor: getColorForCarbonCateory(getCarbonCategory(cleanestPeriod.intensity)),
                          color: getColorForCarbonCateory(getCarbonCategory(cleanestPeriod.intensity)) === '#ffeb00' ? 'black' : 'white',
                          textAlign: 'center'
                        }}>
                          <CardContent sx={{ py: 1.5, px: '0 !important' }}>
                            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                              {cleanestPeriod.intensity.toFixed(0)} g/kWh from {cleanestPeriod.start.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })} to {cleanestPeriod.end.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    )}
                    {dirtiestPeriod && (
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Card sx={{
                          backgroundColor: getColorForCarbonCateory(getCarbonCategory(dirtiestPeriod.intensity)),
                          color: getColorForCarbonCateory(getCarbonCategory(dirtiestPeriod.intensity)) === '#ffeb00' ? 'black' : 'white',
                          textAlign: 'center'
                        }}>
                          <CardContent sx={{ py: 1.5, px: '0 !important' }}>
                            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                              {dirtiestPeriod.intensity.toFixed(0)} g/kWh from {dirtiestPeriod.start.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })} to {dirtiestPeriod.end.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              )}              
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default CarbonIntensityChart;
